/**
 * GrowthDashboardContent — Reusable Growth 2.0 dashboard (tabs only)
 * Used in: /marketing (Growth tab) and /growth (page fallback)
 */
import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart3, TrendingUp, Users, ShoppingCart, Zap, Radio
} from "lucide-react";
import GrowthCEODashboard from "@/components/dashboard/GrowthCEODashboard";
import GrowthSalesBreakdown from "@/components/dashboard/GrowthSalesBreakdown";
import GrowthChannels from "@/components/dashboard/GrowthChannels";
import GrowthCustomers from "@/components/dashboard/GrowthCustomers";
import GrowthProducts from "@/components/dashboard/GrowthProducts";
import GrowthWhatsAppMetrics from "@/components/dashboard/GrowthWhatsAppMetrics";
import GrowthPixelHealth from "@/components/dashboard/GrowthPixelHealth";
import GrowthFutureIntegrations from "@/components/dashboard/GrowthFutureIntegrations";
import GrowthCommandCenter from "@/components/dashboard/GrowthCommandCenter";

const PERIOD_TO_DAYS = {
  today: 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export default function GrowthDashboardContent({ authAxios, showHeader = true, showPeriodSelector = true }) {
  const [activePeriod, setActivePeriod] = useState("30d");
  const normalizedDays = PERIOD_TO_DAYS[activePeriod] ?? 30;

  return (
    <div className="space-y-6 pb-12">
      {/* Header (optional for use in tabs) */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold uppercase tracking-tight">Growth</h1>
            <p className="text-muted-foreground mt-1">Dashboard executivo do restaurante</p>
          </div>
        </div>
      )}

      {/* Period Selector (optional) */}
      {showPeriodSelector && (
        <div className="flex gap-2 bg-secondary p-1 rounded-lg w-fit">
          {["today", "7d", "30d", "90d"].map((p) => (
            <button
              key={p}
              onClick={() => setActivePeriod(p)}
              className={`px-3 py-1 rounded text-sm font-medium transition ${
                activePeriod === p
                  ? "bg-white text-black"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p === "today" ? "Hoje" : p === "7d" ? "7 dias" : p === "30d" ? "30 dias" : "90 dias"}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="ceo" className="w-full">
        <TabsList className="bg-secondary flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="ceo" className="gap-1.5">
            <BarChart3 className="w-4 h-4" />
            CEO
          </TabsTrigger>
          <TabsTrigger value="sales" className="gap-1.5">
            <TrendingUp className="w-4 h-4" />
            Vendas
          </TabsTrigger>
          <TabsTrigger value="channels" className="gap-1.5">
            <Radio className="w-4 h-4" />
            Canais
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-1.5">
            <Users className="w-4 h-4" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-1.5">
            <ShoppingCart className="w-4 h-4" />
            Produtos
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-1.5">
            <span className="w-4 h-4 text-sm">💬</span>
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="pixel" className="gap-1.5">
            <Zap className="w-4 h-4" />
            Pixel
          </TabsTrigger>
          <TabsTrigger value="meta-ads" className="gap-1.5">
            <span className="text-sm">📘</span>
            Meta Ads
          </TabsTrigger>
          <TabsTrigger value="future" className="gap-1.5">
            <TrendingUp className="w-4 h-4" />
            Futuro
          </TabsTrigger>
        </TabsList>

        {/* CEO Dashboard */}
        <TabsContent value="ceo" className="mt-6">
          <GrowthCEODashboard authAxios={authAxios} period={activePeriod} />
        </TabsContent>

        {/* Sales Breakdown */}
        <TabsContent value="sales" className="mt-6">
          <GrowthSalesBreakdown authAxios={authAxios} days={normalizedDays} />
        </TabsContent>

        {/* Channels */}
        <TabsContent value="channels" className="mt-6">
          <GrowthChannels authAxios={authAxios} days={normalizedDays} />
        </TabsContent>

        {/* Customers */}
        <TabsContent value="customers" className="mt-6">
          <GrowthCustomers authAxios={authAxios} period={activePeriod} />
        </TabsContent>

        {/* Products */}
        <TabsContent value="products" className="mt-6">
          <GrowthProducts authAxios={authAxios} days={normalizedDays} />
        </TabsContent>

        {/* WhatsApp */}
        <TabsContent value="whatsapp" className="mt-6">
          <GrowthWhatsAppMetrics days={normalizedDays} />
        </TabsContent>

        {/* Pixel Health */}
        <TabsContent value="pixel" className="mt-6">
          <GrowthPixelHealth authAxios={authAxios} days={normalizedDays} />
        </TabsContent>

        {/* Meta Ads Command Center */}
        <TabsContent value="meta-ads" className="mt-6">
          <GrowthCommandCenter authAxios={authAxios} showHeader={false} showPeriodSelector={true} />
        </TabsContent>

        {/* Future Integrations */}
        <TabsContent value="future" className="mt-6">
          <GrowthFutureIntegrations />
        </TabsContent>
      </Tabs>
    </div>
  );
}
